import { type ReactNode, type Ref } from "react";

import { FormFieldAssistiveText } from "#components/v2/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/v2/shared/FormFieldHeader";
import { useUIVersion } from "#hooks";
import { cn, customTv } from "#utils";
import {
  Input as ReactAriaInput,
  type InputProps as ReactAriaInputProps,
  type TextFieldProps as ReactAriaTextFieldProps,
  TextField as ReactAriaTextField,
} from "react-aria-components";

const inputStyles = customTv({
  base: `
    text-label-medium text-text-primary py-xs outline-none border border-outline-input-enabled bg-primary rounded-1 px-lg leading-md-20 truncate
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    focus:border-outline-input-focus focus:ring-1 focus:ring-outline-input-focus
    disabled:border-outline-input-disabled disabled:cursor-not-allowed disabled:text-text-disabled disabled:bg-background-primary disabled:hover:ring-0 disabled:hover:border-outline-input-disabled
    placeholder:text-text-secondary disabled:placeholder:text-text-disabled
  `,
  variants: {
    variant: {
      primary: "",
    },
    isReadOnly: {
      true: "!cursor-text !bg-background-secondary !text-text-primary !border-transparent hover:ring-0 focus:ring-0",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    uiVersion: {
      v1: "border-legacy-navy-L50 text-legacy-navy-base text-label-medium hover:ring-0 hover:border-legacy-navy-base px-3-5 py-[8.5px] placeholder:text-legacy-navy-L30 focus:border-legacy-orange-base focus:!ring-legacy-orange-base",
      v2: "",
    },
  },
  compoundVariants: [
    {
      uiVersion: "v1",
      isInvalid: true,
      class: "border-outline-negative",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

interface InputProps extends ReactAriaInputProps {
  variant?: "primary";
  className?: string;
}

export interface TextFieldProps extends ReactAriaTextFieldProps {
  inputProps?: InputProps;
  label?: string;
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({
  inputProps,
  label,
  tooltipText,
  className,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  isRequired,
  ref,
  ...rest
}: TextFieldProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaTextField
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      {...rest}
    >
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <ReactAriaInput
        {...inputProps}
        className={inputStyles({
          variant: inputProps?.variant,
          className: inputProps?.className,
          uiVersion,
          isReadOnly,
          isInvalid,
        })}
        placeholder={inputProps?.placeholder ?? "Type here..."}
        ref={ref}
      />
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaTextField>
  );
}
